import { useState, useEffect } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { Link } from 'react-router-dom';
import PrintIcon from '@mui/icons-material/Print';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { generateBillPDF } from "../Sale/BillPrintPDF";
import CircularProgress from '@mui/material/CircularProgress';
import './SaleIndex.css';

function SaleIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { isAuthenticated, companyId, permissions, token, logout } = useAuth();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchOpen, setSearchOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedCurrentDate());
        setToDate(getFormattedCurrentDate());
    }, [isAuthenticated, companyId, token, logout]);

    const hasEditTransactionPermission = permissions.includes('Edit Transaction');
    const hasDeleteTransactionPermission = permissions.includes('Delete Transaction');

    const fetchTableData = async () => {
        try {
            setLoading(true);
            setData([]);
            setCurrentPage(1);
            const response = await fetchData('SaleApi', `/saleDetails/${companyId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Sale Details:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));
    const paginatedData = filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

    const handleDelete = async () => {
        try {
            const response = await deleteData('SaleApi', `${deleteItemId}`, token);
            if (response) {
                fetchTableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            }
        } catch (error) {
            console.error('Error deleting record:', error);
        }
    };

    const handleActivate = async (itemId) => {
        try {
            await activateData('SaleApi', `${itemId}`, token);
            fetchTableData();
        } catch (error) {
            console.error('Error activating record:', error);
        }
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    function formatDate(dateString) {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    }

    function formatTime(timeString) {
        const options = { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false };
        return new Date(`1970-01-01T${timeString}`).toLocaleTimeString('en-GB', options);
    }

    const handlePrint = async (saleid) => {
        try {
            const response = await fetchData('SaleApi', `/SalePrint/${saleid}`, token);
            const labelData = response.response_data;
            const labelsToPrint = Array.isArray(labelData) ? labelData : [labelData];
            for (const d of labelsToPrint) {
                await generateBillPDF(d);
            }
        } catch (error) {
            console.error('Error generating Invoice PDF:', error);
        }
    };

    const getFormattedCurrentDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    const fmt = (val) => (val ? Number(val).toFixed(2) : '0.00');

    const startEntry = filteredData.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
    const endEntry = Math.min(currentPage * rowsPerPage, filteredData.length);

    return (
        <div className="sale-index-container">
            {/* Header */}
            <div className="sale-header-card">
                <div className="sale-header-content">
                    <div className="sale-title-section">
                        <span className="sale-title-icon"><ShoppingCartIcon fontSize="small" /></span>
                        <h1 className="sale-title">Sale Management</h1>
                    </div>

                    <div className="sale-controls">
                        <div className="date-filters">
                            <div className="date-input-group">
                                <label className="date-label">From</label>
                                <input
                                    className="date-input"
                                    type="date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                />
                            </div>
                            <div className="date-input-group">
                                <label className="date-label">To</label>
                                <input
                                    className="date-input"
                                    type="date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className={`search-bar-wrapper${searchOpen ? ' search-bar-open' : ''}`}>
                            <span className="search-bar-icon" onClick={() => setSearchOpen(o => !o)} style={{ cursor: 'pointer' }}>
                                <SearchIcon fontSize="small" />
                            </span>
                            {searchOpen && (
                                <input
                                    autoFocus
                                    className="search-bar-input"
                                    type="text"
                                    placeholder="Search by Bill No, Customer..."
                                    value={searchQuery}
                                    onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                                    onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                                />
                            )}
                        </div>

                        <button className="refresh-btn" onClick={fetchTableData}>
                            <RefreshIcon fontSize="small" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="table-wrapper">
                <TableContainer component={Paper} className="table-container" elevation={0}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className="table-header-cell">Sl.No</TableCell>
                                <TableCell className="table-header-cell">Bill Date</TableCell>
                                <TableCell className="table-header-cell">Bill Time</TableCell>
                                <TableCell className="table-header-cell">Bill Number</TableCell>
                                <TableCell className="table-header-cell">Table Name</TableCell>
                                <TableCell className="table-header-cell">Payment Type</TableCell>
                                <TableCell className="table-header-cell" align="right">Amount</TableCell>
                                <TableCell className="table-header-cell" align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" className="loading-cell">
                                        <div className="loading-container">
                                            <CircularProgress size={36} sx={{ color: '#7c3aed' }} />
                                            <p className="loading-text">Loading sales data...</p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : paginatedData.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" className="no-data-cell">
                                        <p className="no-data-text">No sales data found</p>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedData.map((item, index) => (
                                    <TableRow
                                        key={item.Id}
                                        className={`table-row ${!item.IsActive ? 'inactive-row' : ''}`}
                                    >
                                        <TableCell className="table-cell">
                                            {(currentPage - 1) * rowsPerPage + index + 1}
                                        </TableCell>
                                        <TableCell className="table-cell">{formatDate(item.InvoiceDate)}</TableCell>
                                        <TableCell className="table-cell">{formatTime(item.InvoiceTime)}</TableCell>
                                        <TableCell className="table-cell table-cell-bold">{item.InvoiceNumber}</TableCell>
                                        <TableCell className="table-cell">{item.TableText}</TableCell>
                                        <TableCell className="table-cell">
                                            <span className="payment-badge">{item.PaymentTypeText}</span>
                                        </TableCell>
                                        <TableCell className="table-cell amount-total" align="right">
                                            ₹{item.TotalAmount ? item.TotalAmount.toFixed(2) : '0.00'}
                                        </TableCell>
                                        <TableCell className="table-cell" align="center">
                                            <div className="action-buttons">
                                                <IconButton
                                                    onClick={() => handlePrint(item.Id)}
                                                    className="action-btn print-btn"
                                                    size="small"
                                                    title="Print"
                                                >
                                                    <PrintIcon sx={{ fontSize: 16 }} />
                                                </IconButton>                                                

                                                {hasEditTransactionPermission && (
                                                    <Link to={`/saleEdit/${item.Id}`}>
                                                        <IconButton className="action-btn edit-btn" size="small" title="Edit">
                                                            <EditIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Link>
                                                )}

                                                {hasDeleteTransactionPermission && (
                                                    <IconButton
                                                        onClick={() => (item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id))}
                                                        className={`action-btn ${item.IsActive ? 'delete-btn' : 'activate-btn'}`}
                                                        size="small"
                                                        title={item.IsActive ? 'Delete' : 'Activate'}
                                                    >
                                                        {item.IsActive ? <DeleteIcon sx={{ fontSize: 16 }} /> : <ActivateIcon sx={{ fontSize: 16 }} />}
                                                    </IconButton>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                <div className="pagination-wrapper">
                    <div className="pagination-info">
                        <span>
                            Showing {startEntry} to {endEntry} of {filteredData.length} entries &nbsp; Rows:
                        </span>
                        <select
                            className="rows-select"
                            value={rowsPerPage}
                            onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                        >
                            {[5, 10, 25, 50].map(n => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                    </div>

                    <div className="pagination-controls">
                        <button className="page-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</button>
                        <button className="page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Prev</button>
                        <button className="page-btn page-btn-active">{currentPage}</button>
                        <button className="page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</button>
                        <button className="page-btn" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>Last</button>
                    </div>
                </div>
            </div>

            {/* Delete Confirm Dialog */}
            <Dialog
                open={confirmDialogOpen}
                onClose={closeConfirmDialog}
                PaperProps={{ style: { borderRadius: '10px', padding: '4px' } }}
            >
                <DialogTitle className="dialog-title">Delete Record?</DialogTitle>
                <DialogContent>
                    <DialogContentText className="dialog-text">
                        Are you sure you want to delete this record? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions className="dialog-actions">
                    <button onClick={closeConfirmDialog} className="dialog-btn cancel-btn">Cancel</button>
                    <button onClick={handleDelete} className="dialog-btn confirm-btn">Confirm</button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default SaleIndex;
